import React, { useState } from 'react';
import './PricingModule.css';
import { useNavigate } from 'react-router-dom';


const PricingModule = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [expandedFeatures, setExpandedFeatures] = useState(false);
  const navigate = useNavigate();
  const handleRecruit = () => navigate('/access-recruit');

  const rechargePlans = [
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: 1000,
      period: 'month',
      originalPrice: 1000,
      savings: 0,
      popular: false,
      description: 'Pay monthly, cancel anytime',
      monthlyEquivalent: 1000
    },
    {
      id: 'sixmonths',
      name: '6-Month Plan',
      price: 5000,
      period: '6 months',
      originalPrice: 6000,
      savings: 1000,
      popular: true,
      description: 'Most Popular - Save ₹1,000',
      monthlyEquivalent: 833
    },
    {
      id: 'yearly',
      name: 'Yearly Plan',
      price: 10000,
      period: 'year',
      originalPrice: 12000,
      savings: 2000,
      popular: false,
      description: 'Best Value - Save ₹2,000',
      monthlyEquivalent: 833
    }
  ];

  const allFeatures = [
    {
      category: 'Core HR Management',
      features: [
        'Unlimited employee records',
        'Employee database management',
        'Organizational chart',
        'Employee self-service portal',
        'Document management & storage',
        'Custom fields & forms',
        'Multi-location support',
        'Role-based access control'
      ]
    },
    {
      category: 'Time & Attendance',
      features: [
        'Attendance tracking',
        'Multiple shift management',
        'Overtime calculation',
        'Timesheet management',
        'Geofencing & GPS tracking',
        'Biometric integration',
        'Work from home tracking',
        'Break time management'
      ]
    },
    {
      category: 'Leave Management',
      features: [
        'Leave requests & approvals',
        'Multiple leave types',
        'Leave balance tracking',
        'Holiday calendar',
        'Leave encashment',
        'Carry forward rules',
        'Probation leave policies',
        'Team calendar view'
      ]
    },
    {
      category: 'Recruitment & Onboarding',
      features: [
        'Job posting management',
        'Applicant tracking system (ATS)',
        'Resume parsing',
        'Interview scheduling',
        'Candidate evaluation',
        'Offer letter generation',
        'Digital onboarding workflows',
        'Background verification tracking'
      ]
    },
    {
      category: 'Performance Management',
      features: [
        'Goal setting & OKRs',
        'Performance reviews',
        '360-degree feedback',
        'Continuous feedback',
        'Performance improvement plans',
        'Competency management',
        'Rating scales & templates',
        'Performance analytics'
      ]
    },
    {
      category: 'Payroll Management',
      features: [
        'Salary structure configuration',
        'Automated payroll processing',
        'Statutory compliance (PF, ESI, PT)',
        'Tax calculations (TDS)',
        'Reimbursement management',
        'Bonus & incentive management',
        'Payslip generation & distribution',
        'Bank file generation'
      ]
    },
    {
      category: 'Learning & Development',
      features: [
        'Training program management',
        'Course library',
        'Skill gap analysis',
        'Certification tracking',
        'Training calendar',
        'E-learning integration',
        'Training feedback & evaluation',
        'Learning paths'
      ]
    },
    {
      category: 'Employee Engagement',
      features: [
        'Pulse surveys',
        'Employee feedback',
        'Recognition & rewards',
        'Social feed & announcements',
        'Team collaboration tools',
        'Event management',
        'Birthday & anniversary alerts',
        'Employee wellness programs'
      ]
    },
    {
      category: 'Reports & Analytics',
      features: [
        'Pre-built HR reports',
        'Custom report builder',
        'Real-time dashboards',
        'Attendance reports',
        'Leave reports',
        'Payroll reports',
        'Headcount analytics',
        'Turnover analysis',
        'Export to Excel/PDF'
      ]
    },
    {
      category: 'Integration & Security',
      features: [
        'REST API access',
        'Third-party integrations',
        'SSO (Single Sign-On)',
        'Two-factor authentication',
        'Data encryption',
        'Audit logs',
        'IP restrictions',
        'GDPR compliance tools'
      ]
    },
    {
      category: 'Mobile & Accessibility',
      features: [
        'iOS & Android apps',
        'Mobile attendance marking',
        'Mobile leave requests',
        'Push notifications',
        'Offline mode support',
        'Mobile dashboard',
        'Multi-language support',
        'Responsive web design'
      ]
    },
    {
      category: 'Support & Services',
      features: [
        'Email support',
        'Live chat support',
        'Knowledge base access',
        'Video tutorials',
        'Implementation assistance',
        'Data migration support',
        'Regular product updates',
        'Dedicated account manager (Premium+)'
      ]
    }
  ];

  const pricingTiers = [
    {
      id: 'essential',
      name: 'ESSENTIAL HR',
      price: rechargePlans.find(p => p.id === billingCycle)?.price || 1000,
      period: rechargePlans.find(p => p.id === billingCycle)?.period || 'month',
      popular: false,
      badge: 'MONTHLY'
    },
    {
      id: 'professional',
      name: 'PROFESSIONAL',
      price: (rechargePlans.find(p => p.id === billingCycle)?.price || 1000) * 5,
      period: rechargePlans.find(p => p.id === billingCycle)?.period || 'month',
      popular: false,
      badge: 'SIX MONTH'
    },
    {
      id: 'premium',
      name: 'PREMIUM',
      price: (rechargePlans.find(p => p.id === billingCycle)?.price || 1000) * 10,
      period: rechargePlans.find(p => p.id === billingCycle)?.period || 'month',
      popular: true,
      badge: 'YEARLY'
    },
    {
      id: 'enterprise',
      name: 'ENTERPRISE',
      price: 'Custom',
      period: 'Contact us',
      popular: false,
      isCustom: true,
      badge: 'SALES'
    }
  ];

  return (
    <div className="pricing-module-container">
      {/* Header Section */}
      <div className="pricing-header">
        <h1 className="pricing-main-title">
          Smarter HR solution,
          tailored for every business
        </h1>
        <div className="pricing-benefits">
          <div className="benefit-item">
            <span className="benefit-check">✓</span>
            <span>30-day free trial</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-check">✓</span>
            <span>No forced contracts</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-check">✓</span>
            <span>No credit card required</span>
          </div>
          <div className="benefit-item">
            <span className="benefit-check">✓</span>
            <span>Hassle-free setup</span>
          </div>
        </div>
      </div>

      {/* Free Edition Banner */}
      <div className="pricing-free-banner">
        <div className="free-banner-content">
          <p className="free-banner-text">
            Explore our <span className="free-highlight">FREE edition</span>, forever free for up to 5 users.
          </p>
        </div>
        <button className="free-banner-btn" onClick={handleRecruit}>GET STARTED</button>
      </div>

      {/* Billing Toggle */}
      {/* <div className="pricing-billing-toggle">
        <button 
          className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
          onClick={() => setBillingCycle('monthly')}
        >
          Monthly
        </button>
        <button 
          className={`toggle-btn ${billingCycle === 'sixmonths' ? 'active' : ''}`}
          onClick={() => setBillingCycle('sixmonths')}
        >
          6 Months
        </button>
        <button 
          className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
          onClick={() => setBillingCycle('yearly')}
        >
          Yearly
        </button>
        {billingCycle !== 'monthly' && (
          <span className="savings-badge">
            Save ₹{rechargePlans.find(p => p.id === billingCycle)?.savings}
          </span>
        )}
      </div> */}

      {/* Pricing Cards */}
      <div className="pricing-cards-grid">
        {pricingTiers.map((tier) => (
          <div 
            key={tier.id} 
            className={`pricing-card ${tier.popular ? 'popular' : ''}`}
          >
            <div className="popular-badge1">{tier.badge}</div>
            
            <div className="pricing-card-header">
              <h3 className="tier-name">{tier.name}</h3>
              <div className="tier-price">
                {tier.isCustom ? (
                  <div className="custom-price">Contact Us</div>
                ) : (
                  <>
                    <span className="currency">₹</span>
                    <span className="amount">{tier.price}</span>
                  </>
                )}
              </div>
              <p className="tier-period">
                {tier.isCustom ? 'Custom pricing for your needs' : `/${tier.period} billed annually`}
              </p>
              <button className="try-btn" onClick={handleRecruit}>
                {tier.isCustom ? 'CONTACT SALES' : 'TRY FOR FREE'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Expandable Features Section */}
      <div className="features-expand-section">
        <button 
          className="expand-features-btn"
          onClick={() => setExpandedFeatures(!expandedFeatures)}
        >
          {expandedFeatures ? 'Collapse all features ↑' : 'Expand all features ↓'}
        </button>

        <div className={`all-features-container ${expandedFeatures ? 'expanded' : ''}`}>
          <div className="all-features-header">
            <h2>Complete Feature List - All Plans Include</h2>
            <p className="features-subtitle">
              All recharge plans include unlimited access to these features
            </p>
          </div>

          <div className="features-grid">
            {allFeatures.map((category, idx) => (
              <div key={idx} className="feature-category">
                <h3 className="category-title">{category.category}</h3>
                <ul className="category-features-list">
                  {category.features.map((feature, index) => (
                    <li key={index} className="category-feature-item">
                      <span className="feature-check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="pricing-footer">
        {/* <p className="footer-note">
          Local taxes (VAT, GST, etc.) will be charged in addition to the prices mentioned.
        </p> */}
      </div>
    </div>
  );
};

export default PricingModule;